import { FC } from "react";
import { Entry } from "contentful";
import styled from "styled-components";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { Document } from "@contentful/rich-text-types";

export interface AnnouncementBarSlideEntry extends Entry {
  fields: {
    name: string;
    text?: Document;
    media?: {
      fields: {
        file: {
          url: string;
          contentType: string;
        };
        title: string;
      };
    };
    alignment?: string;
  };
}

interface AnnouncementBarSlideProps {
  slide: AnnouncementBarSlideEntry;
  brand: string;
  theme?: string;
}

const SlideFigure = styled.figure<{
  $alignment?: string;
}>`
  display: flex;
  align-items: center;
  justify-content: ${({ $alignment }) =>
    $alignment == 'left' ? 'flex-start' :
    $alignment == 'right' ? 'flex-end' : 'center'};
  height: 100%;
  width: 100%;
  padding: 0;
  margin: 0;
  background-color: transparent;
`;

const SlideContent = styled.div<{ $alignment?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  gap: 10px;
  overflow: hidden;
`;

const SlideInner = styled.div<{ $alignment?: string }>`
  max-width: 550px;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: ${({ $alignment }) =>
    $alignment == 'left' ? 'flex-start' :
    $alignment == 'right' ? 'flex-end' : 'center'};
`;

const SlideImageWrapper = styled.div`
  padding: 4px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
`;

const SlideImage = styled.img`
  max-height: 20px;
  width: auto;
  object-fit: contain;
  display: block;
`;

const TextContent = styled.div<{ $brand: string; theme?: string }>`
  transition: opacity 0.6s ease-in-out;
  opacity: 1;

  p, a {
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    ${({ $brand, theme }) => {
      switch ($brand) {
        case 'fender':
          return `
            color: ${theme == 'dark' ? '#F4F4F4' : '#191A1F'};
            font-size: 12px;
            line-height: 18px;
            letter-spacing: .12px;

            @media (min-width: 768px) {
              font-size: 14px;
              font-weight: 400;
              line-height: 20px;
              letter-spacing: .14px;
            }
          `;
        case 'presonus':
          return `
            color: ${theme == 'dark' ? '#FFF' : '#000'};
          `;
        case 'jackson':
          return `
            color: ${theme == 'dark' ? '#FFF' : '#4a4a4a'};
            font-size: 16px;
            letter-spacing: .5px;
            font-weight: 700;
          `;
        default:
          return `color: ${theme == 'dark' ? '#F4F4F4' : '#191A1F'};`;
      }
    }}
  }

  a {
    text-decoration-line: underline;
  }
`;

const AnnouncementBarSlide: FC<AnnouncementBarSlideProps> = ({
  slide: {
    fields: { media, text, alignment },
  },
  brand,
  theme
}) => {
  return (
    <SlideFigure
      className="announcement-bar"
      $alignment={alignment}
    >
      <SlideContent $alignment={alignment}>
        <SlideInner $alignment={alignment}>
          {media?.fields?.file?.url && (
            <SlideImageWrapper>
              <SlideImage src={media.fields.file.url} alt={media.fields.title} />
            </SlideImageWrapper>
          )}
          {text && (
            <TextContent $brand={brand} theme={theme}>
              {documentToReactComponents(text)}
            </TextContent>
          )}
        </SlideInner>
      </SlideContent>
    </SlideFigure>
  );
};

export default AnnouncementBarSlide;